import bootstrap from 'bootstrap/dist/js/bootstrap.bundle.min.js';

const ERROR_MESSAGES = {
	ELEMENT_NOT_FOUND: 'Navbar: Element not found',
	INIT_FAILED: 'Navbar: Initialization failed',
};

const CONFIG = {
	SCROLL_BEHAVIOR: {
		DOWN: 'down',
		UP: 'up',
	},
	CLASSES: {
		HIDDEN: 'navbar--hidden',
		SCROLLED: 'navbar--scrolled',
	},
};

/**
 * Initialize navbar functionality
 * @returns {void}
 */
const initializeNavbar = () => {
	const navbar = document.querySelector('.navbar');
	if (!navbar) {
		console.warn(ERROR_MESSAGES.ELEMENT_NOT_FOUND);
		return;
	}

	try {
		// Handle scroll behavior
		let lastScrollTop = 0;

		/**
		 * Handle scroll events for navbar visibility
		 * @returns {void}
		 */
		const handleScroll = () => {
			const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
			const scrollDirection = scrollTop > lastScrollTop ? CONFIG.SCROLL_BEHAVIOR.DOWN : CONFIG.SCROLL_BEHAVIOR.UP;

			if (scrollDirection === CONFIG.SCROLL_BEHAVIOR.DOWN && scrollTop > navbar.offsetHeight) {
				navbar.classList.add(CONFIG.CLASSES.HIDDEN);
			} else {
				navbar.classList.remove(CONFIG.CLASSES.HIDDEN);
			}

			// Update scroll position
			lastScrollTop = scrollTop;

			// Add/remove background when scrolled
			navbar.classList.toggle(CONFIG.CLASSES.SCROLLED, scrollTop > 0);
		};

		// Add scroll event listener
		window.addEventListener('scroll', handleScroll, { passive: true });

		// Initialize state
		handleScroll();

		// Offcanvas toggler logic
		const toggler = document.getElementById('offcanvasToggler');
		const offcanvasEl = document.getElementById('navDrawer');
		let offcanvasInstance = null;

		if (offcanvasEl && toggler) {
			offcanvasInstance = bootstrap.Offcanvas.getOrCreateInstance(offcanvasEl);

			// Show/hide icon logic
			offcanvasEl.addEventListener('show.bs.offcanvas', () => {
				toggler.classList.add('is-open');
			});
			offcanvasEl.addEventListener('hide.bs.offcanvas', () => {
				toggler.classList.remove('is-open');
			});

			// Toggler click: if open, close
			toggler.addEventListener('click', (e) => {
				if (offcanvasEl.classList.contains('show')) {
					e.preventDefault();
					offcanvasInstance.hide();
				}
				// else, Bootstrap will handle opening
			});

			// Close offcanvas when any link inside is clicked
			offcanvasEl.addEventListener('click', (e) => {
				if (e.target.tagName === 'A') {
					offcanvasInstance.hide();
				}
			});
		}

		// Search dropdown functionality
		initializeSearchDropdown();
	} catch (error) {
		console.error(ERROR_MESSAGES.INIT_FAILED, error);
	}
};

/**
 * Initialize search dropdown functionality
 * @returns {void}
 */
const initializeSearchDropdown = () => {
	const searchDropdown = document.getElementById('searchDropdownOverlay');
	const searchInput = document.getElementById('searchInput');
	const searchForm = document.getElementById('searchDropdownForm');
	const popularTags = document.querySelectorAll('.popular-tag');
	const closeButton = document.getElementById('searchDropdownClose');
	const searchButton = document.getElementById('searchDropdown');

	if (!searchDropdown || !searchInput || !searchButton) {
		console.warn('Search dropdown elements not found');
		return;
	}

	if (searchDropdown.dataset.searchDropdownInitialized === 'true') {
		return;
	}

	searchDropdown.dataset.searchDropdownInitialized = 'true';

	/**
	 * Update header height CSS custom property
	 * @returns {void}
	 */
	const updateHeaderHeight = () => {
		const header = document.querySelector('.header');
		if (header) {
			const headerHeight = header.offsetHeight;
			document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
		}
	};

	/**
	 * Show the search dropdown
	 * @returns {void}
	 */
	const showDropdown = () => {
		// Update header height before showing
		updateHeaderHeight();

		searchDropdown.classList.add('show');
		searchButton.setAttribute('aria-expanded', 'true');

		// Focus and select text after animation
		setTimeout(() => {
			searchInput.focus();
			searchInput.select();
		}, 200);
	};

	/**
	 * Hide the search dropdown
	 * @returns {void}
	 */
	const hideDropdown = () => {
		searchDropdown.classList.remove('show');
		searchButton.setAttribute('aria-expanded', 'false');
	};

	/**
	 * Toggle the search dropdown
	 * @returns {void}
	 */
	const toggleDropdown = () => {
		if (searchDropdown.classList.contains('show')) {
			hideDropdown();
		} else {
			showDropdown();
		}
	};

	/**
	 * Handle popular tag clicks
	 * @param {Event} e - Click event
	 * @returns {void}
	 */
	const handlePopularTagClick = (e) => {
		const tagText = e.target.textContent.trim();
		searchInput.value = tagText;
		searchInput.focus();
	};

	/**
	 * Handle search form submission
	 * @param {Event} e - Submit event
	 * @returns {void}
	 */
	const handleSearchSubmit = (e) => {
		const searchTerm = searchInput.value.trim();

		if (!searchTerm) {			
			e.preventDefault();
		}
	};

	/**
	 * Handle escape key
	 * @param {KeyboardEvent} e - Keydown event
	 * @returns {void}
	 */
	const handleKeydown = (e) => {
		if (e.key === 'Escape' && searchDropdown.classList.contains('show')) {
			hideDropdown();
		}
	};

	/**
	 * Handle clicks outside the dropdown
	 * @param {Event} e - Click event
	 * @returns {void}
	 */
	const handleClickOutside = (e) => {
		const container = document.querySelector('.search-dropdown-container');
		if (searchDropdown.classList.contains('show') && !container.contains(e.target)) {
			hideDropdown();
		}
	};

	// Add event listeners
	searchButton.addEventListener('click', (e) => {
		e.preventDefault();
		e.stopPropagation();
		toggleDropdown();
	});

	if (closeButton) {
		closeButton.addEventListener('click', () => {
			hideDropdown();
		});
	}

	if (searchForm) {
		searchForm.addEventListener('submit', handleSearchSubmit);
	}

	popularTags.forEach((tag) => {
		tag.addEventListener('click', handlePopularTagClick);
	});

	// Global event listeners
	document.addEventListener('keydown', handleKeydown);
	document.addEventListener('click', handleClickOutside);

	// Update header height on window resize
	window.addEventListener('resize', updateHeaderHeight);

	// Set initial header height
	updateHeaderHeight();
};

export { initializeNavbar };
